
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, ArrowRight, Activity, Globe, Loader2, AlertCircle, 
  MoreHorizontal, Search, Trash2, Edit2, Lock, X, ShieldCheck, 
  Link as LinkIcon, Users, UserCog, Crown, ShieldAlert, Settings,
  CheckCircle, Ban, Power, Accessibility
} from 'lucide-react';
import { getDomains, saveDomain, deleteDomain, updateDomain, getAllUsers, updateUserRole, updateUserStatus } from '../services/storageService';
import { Domain } from '../types';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const REQUIRED_PASSWORD = '2360';

const Dashboard: React.FC = () => {
  const { user, role } = useAuth();
  const isAdmin = role === 'admin';
  
  const [domains, setDomains] = useState<Domain[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'projects' | 'users'>('projects');
  
  // Create Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newDomainName, setNewDomainName] = useState('');
  const [newDomainUrl, setNewDomainUrl] = useState('');
  const [enableGdpr, setEnableGdpr] = useState(false);
  
  // Edit State
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState<Domain | null>(null);
  const [editName, setEditName] = useState('');
  const [editUrl, setEditUrl] = useState('');
  
  // Password Modal State
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [pendingAction, setPendingAction] = useState<{
    type: 'delete' | 'update' | 'role' | 'status';
    targetId: string;
    payload?: any;
  } | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useLanguage();

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const domainsData = await getDomains();
      setDomains(domainsData);
      
      if (isAdmin) {
        const usersData = await getAllUsers();
        setAllUsers(usersData);
      }
    } catch (err: any) {
      console.error("Failed to load data:", err);
      setError(err.message || "Failed to connect to the database.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [role]);

  const toggleMenu = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveMenuId(activeMenuId === id ? null : id);
  };

  const handleAddDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomainName || !newDomainUrl) return;

    setIsSaving(true);
    let formattedUrl = newDomainUrl.trim();
    if (!/^https?:\/\//i.test(formattedUrl)) {
      formattedUrl = `https://${formattedUrl}`;
    }

    const newDomain = {
      name: newDomainName,
      url: formattedUrl,
      createdAt: Date.now(),
      gdprConsentEnabled: enableGdpr,
      accessibilityEnabled: false,
      accessibilitySettings: {
        enabled: false,
        buttonPosition: 'bottom-left' as const,
        primaryColor: '#4f46e5',
        buttonIcon: 'universal' as const
      }
    };

    try {
      await saveDomain(newDomain);
      await loadData();
      setIsModalOpen(false);
      setNewDomainName('');
      setNewDomainUrl('');
      setEnableGdpr(false);
    } catch (err: any) {
      alert("Error creating domain: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const initiateEdit = (e: React.MouseEvent, domain: Domain) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveMenuId(null);
    setEditingDomain(domain);
    setEditName(domain.name);
    setEditUrl(domain.url);
    setIsEditModalOpen(true);
  };

  const initiateDelete = (e: React.MouseEvent, domainId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveMenuId(null);
    setPendingAction({
      type: 'delete',
      targetId: domainId
    });
    setPasswordInput('');
    setPasswordError('');
    setIsPasswordModalOpen(true);
  };

  const confirmEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDomain) return;

    let formattedUrl = editUrl.trim();
    if (!/^https?:\/\//i.test(formattedUrl)) {
      formattedUrl = `https://${formattedUrl}`;
    }

    setIsEditModalOpen(false);
    setPendingAction({
      type: 'update',
      targetId: editingDomain.id,
      payload: { name: editName, url: formattedUrl }
    });
    setPasswordInput('');
    setPasswordError('');
    setIsPasswordModalOpen(true);
  };

  const handleUpdateRole = (targetUid: string, currentRole: string) => {
    const nextRole = currentRole === 'admin' ? 'user' : 'admin';
    setPendingAction({
      type: 'role',
      targetId: targetUid,
      payload: { role: nextRole }
    });
    setPasswordInput('');
    setPasswordError('');
    setIsPasswordModalOpen(true);
  };

  const handleToggleStatus = (targetUid: string, currentStatus: boolean) => {
    setPendingAction({
      type: 'status',
      targetId: targetUid,
      payload: { isActive: !currentStatus }
    });
    setPasswordInput('');
    setPasswordError('');
    setIsPasswordModalOpen(true);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput !== REQUIRED_PASSWORD) {
      setPasswordError("Incorrect password");
      return;
    }

    if (!pendingAction) return;
    setIsSaving(true);
    setIsPasswordModalOpen(false);

    try {
      if (pendingAction.type === 'delete') {
        await deleteDomain(pendingAction.targetId);
      } else if (pendingAction.type === 'update') {
        await updateDomain(pendingAction.targetId, pendingAction.payload);
      } else if (pendingAction.type === 'role') {
        await updateUserRole(pendingAction.targetId, pendingAction.payload.role);
      } else if (pendingAction.type === 'status') {
        await updateUserStatus(pendingAction.targetId, pendingAction.payload.isActive);
      }
      await loadData();
    } catch (err: any) {
      alert("Operation failed: " + err.message);
    } finally {
      setIsSaving(false);
      setPendingAction(null);
      setEditingDomain(null);
    }
  };

  const filteredDomains = domains.filter(domain => 
    domain.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    domain.url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = allUsers.filter(u => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout onSearch={setSearchTerm}>
      <div className="max-w-7xl mx-auto" onClick={() => setActiveMenuId(null)}>
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 md:mb-10 gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
                {isAdmin ? 'Administration' : t.dashboardTitle}
              </h1>
              {isAdmin && (
                <div className="bg-indigo-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 uppercase tracking-widest shadow-lg shadow-indigo-500/30">
                  <Crown size={10} /> Super Admin
                </div>
              )}
            </div>
            <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm md:text-base">
              {isAdmin ? 'Managing all properties and system users' : t.dashboardSubtitle}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {isAdmin && (
              <div className="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl flex w-full sm:w-auto">
                <button 
                  onClick={() => setActiveTab('projects')}
                  className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'projects' ? 'bg-white dark:bg-slate-700 text-indigo-600 shadow-sm' : 'text-slate-500'}`}
                >
                  Projects
                </button>
                <button 
                  onClick={() => setActiveTab('users')}
                  className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'users' ? 'bg-white dark:bg-slate-700 text-indigo-600 shadow-sm' : 'text-slate-500'}`}
                >
                  Users
                </button>
              </div>
            )}
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-indigo-500/30 text-sm md:text-base"
            >
              <Plus size={20} strokeWidth={3} />
              {t.addNewProject}
            </button>
          </div>
        </div>

        {activeTab === 'projects' ? (
          <>
            {isLoading ? (
              <div className="flex justify-center py-40"><Loader2 className="animate-spin text-indigo-500" size={48} /></div>
            ) : filteredDomains.length === 0 ? (
              <div className="text-center py-20 md:py-32 bg-white dark:bg-slate-900 rounded-[32px] border-2 border-dashed border-slate-200 dark:border-slate-800 p-6">
                <Activity className="text-slate-300 mx-auto mb-4 md:mb-6" size={40} />
                <h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white">{t.noProjects}</h3>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filteredDomains.map((domain) => (
                  <div key={domain.id} className="relative group">
                    <Link
                      to={`/report/${domain.id}`}
                      className="block bg-white dark:bg-slate-900 p-6 md:p-8 rounded-[32px] border border-slate-100 dark:border-slate-800 hover:shadow-xl hover:-translate-y-1 transition-all h-full"
                    >
                      <div className="flex justify-between items-start mb-6">
                        <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl text-indigo-600 dark:text-indigo-400 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                          <Globe size={28} />
                        </div>
                        <div className="flex items-center gap-2 min-w-0">
                          {isAdmin && (
                            <div className="text-[9px] font-bold text-slate-400 bg-slate-50 dark:bg-slate-800 px-2 py-1 rounded-md truncate max-w-[80px]">
                              {(domain as any).userEmail || 'System'}
                            </div>
                          )}
                          <button 
                            onClick={(e) => toggleMenu(e, domain.id)}
                            className="p-2 text-slate-300 dark:text-slate-600 hover:text-indigo-500 dark:hover:text-indigo-400 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition-all"
                          >
                            <MoreHorizontal size={24} />
                          </button>
                        </div>
                      </div>
                      
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white truncate flex-1">{domain.name}</h3>
                          <div className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 p-1 rounded-md shrink-0">
                            <Lock size={14} />
                          </div>
                        </div>
                        <p className="text-slate-400 text-xs md:text-sm truncate mb-6">{domain.url}</p>
                      </div>
                      
                      <div className="flex items-center justify-between pt-6 border-t border-slate-50 dark:border-slate-800">
                        <div className="text-xs font-medium text-emerald-600 flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                          Live
                        </div>
                        <div className="text-xs md:text-sm text-slate-400 group-hover:text-indigo-600 font-bold flex items-center gap-1 transition-colors">
                          {t.viewReport} <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
                        </div>
                      </div>
                    </Link>

                    {/* Action Dropdown Menu */}
                    {activeMenuId === domain.id && (
                      <div className="absolute top-16 right-4 z-30 w-52 bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                        <div className="p-1.5">
                          <button 
                            onClick={(e) => initiateEdit(e, domain)}
                            className="w-full text-left px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-xl transition-colors flex items-center gap-2"
                          >
                            <Edit2 size={16} /> Edit Details
                          </button>
                          <Link
                            to={`/report/${domain.id}/gdpr-settings`}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full text-left px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-xl transition-colors flex items-center gap-2"
                          >
                            <Settings size={16} /> GDPR Settings
                          </Link>
                          <Link
                            to={`/report/${domain.id}/accessibility`}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full text-left px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-xl transition-colors flex items-center gap-2"
                          >
                            <Accessibility size={16} /> Accessibility
                          </Link>
                          <div className="h-px bg-slate-50 dark:bg-slate-800 my-1 mx-2" />
                          <button 
                            onClick={(e) => initiateDelete(e, domain.id)}
                            className="w-full text-left px-3 py-2.5 text-sm font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-colors flex items-center gap-2"
                          >
                            <Trash2 size={16} /> Delete Project
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* User Management Tab */
          <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-slate-100 dark:border-slate-800 overflow-x-auto shadow-sm">
            <table className="w-full text-left min-w-[700px]">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 text-[10px] md:text-xs font-bold uppercase tracking-wider">
                  <th className="px-6 md:px-8 py-4">User Email</th>
                  <th className="px-6 md:px-8 py-4">Role</th>
                  <th className="px-6 md:px-8 py-4">Status</th>
                  <th className="px-6 md:px-8 py-4">Last Login</th>
                  <th className="px-6 md:px-8 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredUsers.map((u) => (
                  <tr key={u.uid} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 md:px-8 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 shrink-0">
                          <Users size={16} />
                        </div>
                        <span className="font-bold text-slate-900 dark:text-white truncate max-w-[150px] md:max-w-none text-sm md:text-base">{u.email}</span>
                        {u.email === 'mbui41@gmail.com' && <Crown size={14} className="text-amber-500 shrink-0" />}
                      </div>
                    </td>
                    <td className="px-6 md:px-8 py-5">
                      <span className={`px-3 py-1 rounded-full text-[9px] md:text-[10px] font-bold uppercase tracking-widest ${u.role === 'admin' ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-6 md:px-8 py-5">
                      <span className={`flex items-center gap-1.5 text-[9px] md:text-[10px] font-bold uppercase tracking-widest ${u.isActive ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {u.isActive ? <CheckCircle size={12} /> : <Ban size={12} />}
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 md:px-8 py-5 text-xs md:text-sm text-slate-400">
                      {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 md:px-8 py-5 text-right">
                      {u.email !== 'mbui41@gmail.com' && (
                        <div className="flex justify-end gap-1 md:gap-2">
                          <button 
                            onClick={() => handleToggleStatus(u.uid, u.isActive)}
                            className={`p-2 transition-colors rounded-lg ${u.isActive ? 'text-slate-400 hover:text-rose-500 hover:bg-rose-50' : 'text-emerald-500 hover:bg-emerald-50'}`}
                            title={u.isActive ? 'Deactivate User' : 'Activate User'}
                          >
                            <Power className="w-4.5 h-4.5 md:w-5 md:h-5" />
                          </button>
                          <button 
                            onClick={() => handleUpdateRole(u.uid, u.role)}
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors rounded-lg"
                            title="Toggle Admin/User Role"
                          >
                            <UserCog className="w-4.5 h-4.5 md:w-5 md:h-5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Project Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-[32px] w-full max-w-md p-6 md:p-8 shadow-2xl border border-slate-100 dark:border-slate-800">
              <h2 className="text-xl md:text-2xl font-bold mb-6 text-slate-900 dark:text-white">{t.newProjectModal}</h2>
              <form onSubmit={handleAddDomain}>
                <div className="space-y-5">
                  <div>
                    <label className="block text-xs md:text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">{t.websiteName}</label>
                    <input
                      type="text"
                      required
                      className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white transition-all text-sm md:text-base"
                      value={newDomainName}
                      onChange={(e) => setNewDomainName(e.target.value)}
                      placeholder="My Portfolio"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">{t.domainUrl}</label>
                    <input
                      type="text"
                      required
                      className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white transition-all text-sm md:text-base"
                      value={newDomainUrl}
                      onChange={(e) => setNewDomainUrl(e.target.value)}
                      placeholder="mysite.com"
                    />
                    <p className="mt-2 text-[10px] text-slate-400 flex items-center gap-1.5">
                      <ShieldCheck size={12} className="text-emerald-500 shrink-0" />
                      Domain-locked for security.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 mt-8">
                  <button 
                    type="button" 
                    onClick={() => setIsModalOpen(false)} 
                    className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold rounded-xl transition-colors border border-slate-200 dark:border-slate-700 text-sm md:text-base"
                  >
                    {t.cancel}
                  </button>
                  <button type="submit" disabled={isSaving} className="flex-1 px-4 py-3 bg-indigo-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-indigo-500/20 text-sm md:text-base">
                    {isSaving ? <Loader2 size={20} className="animate-spin mx-auto" /> : t.createProject}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Project Modal */}
        {isEditModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-[32px] w-full max-w-md p-6 md:p-8 shadow-2xl border border-slate-100 dark:border-slate-800">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white">Edit Project</h2>
                <button onClick={() => setIsEditModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
              </div>
              
              <form onSubmit={confirmEdit}>
                <div className="space-y-5">
                  <div>
                    <label className="block text-xs md:text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Project Name</label>
                    <input
                      type="text"
                      required
                      className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white transition-all text-sm md:text-base"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Whitelisted Domain URL</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <LinkIcon size={16} />
                      </div>
                      <input
                        type="text"
                        required
                        className="w-full p-3 pl-10 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white transition-all text-sm md:text-base"
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                      />
                    </div>
                    <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20 rounded-xl">
                      <p className="text-[10px] text-amber-700 dark:text-amber-400 leading-relaxed font-medium flex items-start gap-2">
                        <AlertCircle size={14} className="shrink-0 mt-0.5" />
                        Changing this URL updates the security whitelist. Traffic from the old domain will be blocked.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3 mt-8">
                  <button 
                    type="button" 
                    onClick={() => setIsEditModalOpen(false)} 
                    className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold rounded-xl transition-colors border border-slate-200 dark:border-slate-700 text-sm md:text-base"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 px-4 py-3 bg-indigo-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-indigo-500/20 text-sm md:text-base">
                    Update Details
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Password Modal */}
        {isPasswordModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-[32px] w-full max-w-sm p-8 shadow-2xl border border-slate-100 dark:border-slate-800">
              <div className="text-center mb-6">
                <ShieldAlert className="mx-auto mb-4 text-rose-500" size={40} />
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Admin Verification</h2>
                <p className="text-slate-500 text-sm mt-1">This action requires the system master key.</p>
              </div>
              <form onSubmit={handlePasswordSubmit}>
                <input
                  type="password"
                  autoFocus
                  className="w-full p-3 text-center text-2xl tracking-widest font-bold bg-slate-50 dark:bg-slate-800 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all dark:text-white"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="••••"
                />
                {passwordError && <p className="text-rose-500 text-xs text-center mt-2 font-bold">{passwordError}</p>}
                <div className="flex gap-3 mt-6">
                  <button 
                    type="button" 
                    onClick={() => { setIsPasswordModalOpen(false); setPendingAction(null); }} 
                    className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 font-bold rounded-xl text-slate-600 dark:text-slate-300 transition-colors border border-slate-200 dark:border-slate-700 text-sm"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold rounded-xl transition-all text-sm">Confirm</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
